export interface TaskListControllerActions {
  bindTimeline?(root: ParentNode): void;
  selectionMode: boolean;
  selectTask: (id: string, selected: boolean) => void | Promise<void>;
  openTask: (id: string) => void | Promise<void>;
  rememberBoardColumn: (collectionId: string, column: string) => void;
  captureBoardTask: (form: HTMLFormElement, openEditor: boolean) => void | Promise<void>;
  cancelBoardCapture: () => void | Promise<void>;
  jumpToGroup?(id: string): void;
  taskDetailsToggled?(open: boolean): void;
  composerGroupToggled?(group: string, open: boolean): void;
  openDependency?(id: string): void | Promise<void>;
  createChildTask?(parentId: string, title: string): void | Promise<void>;
  reorderChildTask?(parentId: string, childId: string, direction: -1 | 1): void | Promise<void>;
  attachChildTask?(parentId: string): void | Promise<void>;
  detachChildTask?(childId: string): void | Promise<void>;
  resolveConflicts?(id: string): void | Promise<void>;
  loadPreviews?(id: string, trigger: HTMLElement): void | Promise<void>;
  createFromTemplate?(id: string): void | Promise<void>;
  removeSubtask?(row: HTMLElement, list: HTMLElement, next: ChildNode | null): void;
  moveTask?(id: string, direction: -1 | 1): void | Promise<void>;
  moveBoardTaskToColumn?(id: string, column: string | null): void | Promise<void>;
  shiftTimeline?(collectionId: string, windows: number): void | Promise<void>;
  jumpTimeline?(collectionId: string, date: string): void | Promise<void>;
  resetTimeline?(collectionId: string): void | Promise<void>;
  showMoreTimeline?(collectionId: string): void | Promise<void>;
  moveSubtask?(row: HTMLElement, list: HTMLElement, direction: -1 | 1, originalIds: string[]): void;
}

function invoke(action: () => void | Promise<void>): void { void action(); }

export function bindTaskListController(root: ParentNode, actions: TaskListControllerActions): void {
  actions.bindTimeline?.(root);
  root.querySelectorAll<HTMLInputElement>("[data-select-task]").forEach(input => input.addEventListener("change", () => invoke(() => actions.selectTask(input.dataset.selectTask || "", input.checked))));

  const bindTask = (item: HTMLElement, selector: ".todo-row" | ".kanban-card") => {
    item.addEventListener("click", event => {
      if (selector === ".todo-row" && item.dataset.swiped === "true") { delete item.dataset.swiped; return; }
      if ((event.target as HTMLElement).closest("button, input, select, label, summary, .drag-handle, .bulk-selector")) return;
      if (actions.selectionMode) {
        const input = item.querySelector<HTMLInputElement>("[data-select-task]:not(:disabled)");
        if (input) invoke(() => actions.selectTask(input.dataset.selectTask || "", !input.checked));
        return;
      }
      invoke(() => actions.openTask(item.dataset.todo || ""));
    });
    if (!actions.selectionMode) return;
    item.addEventListener("keydown", event => {
      if (event.key !== " " && event.key !== "Enter") return;
      if ((event.target as HTMLElement).closest("button,input,.bulk-selector")) return;
      event.preventDefault();
      const input = item.querySelector<HTMLInputElement>("[data-select-task]:not(:disabled)");
      if (input) invoke(() => actions.selectTask(input.dataset.selectTask || "", !input.checked));
    });
  };
  root.querySelectorAll<HTMLElement>(".todo-row").forEach(row => bindTask(row, ".todo-row"));
  root.querySelectorAll<HTMLElement>(".kanban-card").forEach(card => bindTask(card, ".kanban-card"));
  root.querySelectorAll<HTMLSelectElement>("[data-board-move-to]").forEach(select => select.addEventListener("change", () => {
    if (select.value === "__choose") return;
    invoke(() => actions.moveBoardTaskToColumn?.(select.dataset.boardMoveTo || "", select.value || null));
  }));

  root.querySelectorAll<HTMLElement>(".kanban-column").forEach(column => {
    const remember = () => actions.rememberBoardColumn(column.dataset.boardCollection || "", column.dataset.boardColumn || "");
    column.addEventListener("focusin", remember);
    column.addEventListener("pointerdown", remember);
  });
  const timelineId = (element: HTMLElement) => element.closest<HTMLElement>("[data-timeline-collection]")?.dataset.timelineCollection || "";
  root.querySelectorAll<HTMLElement>("[data-timeline-shift]").forEach(button => button.addEventListener("click", () => invoke(() => actions.shiftTimeline?.(timelineId(button), Number(button.dataset.timelineShift)) || undefined)));
  root.querySelectorAll<HTMLElement>("[data-timeline-today]").forEach(button => button.addEventListener("click", () => invoke(() => actions.resetTimeline?.(timelineId(button)) || undefined)));
  root.querySelectorAll<HTMLInputElement>("[data-timeline-jump]").forEach(input => input.addEventListener("change", () => { if (input.value) invoke(() => actions.jumpTimeline?.(timelineId(input), input.value) || undefined); }));
  root.querySelectorAll<HTMLElement>("[data-timeline-more]").forEach(button => button.addEventListener("click", () => invoke(() => actions.showMoreTimeline?.(timelineId(button)) || undefined)));
  root.querySelectorAll<HTMLButtonElement>("[data-timeline-family]").forEach(button => button.addEventListener("click", () => {
    const lanes = Array.from(root.querySelectorAll<HTMLElement>("[data-timeline-child-lanes]")).find(item => item.dataset.timelineChildLanes === button.dataset.timelineFamily);
    if (!lanes) return;
    lanes.hidden = !lanes.hidden;
    button.setAttribute("aria-expanded", String(!lanes.hidden));
    button.textContent = lanes.hidden ? "▸" : "▾";
  }));
  root.querySelectorAll<HTMLFormElement>("[data-board-capture]").forEach(form => {
    form.addEventListener("submit", event => { event.preventDefault(); invoke(() => actions.captureBoardTask(form, false)); });
    form.addEventListener("keydown", event => {
      if (event.key === "Escape") { event.preventDefault(); invoke(actions.cancelBoardCapture); return; }
      if (event.key !== "Enter" || event.isComposing) return;
      event.preventDefault();
      invoke(() => actions.captureBoardTask(form, event.shiftKey));
    });
  });
  root.querySelector<HTMLSelectElement>("[data-board-jump]")?.addEventListener("change", event => {
    const select = event.currentTarget as HTMLSelectElement;
    const board = root.querySelector<HTMLElement>(".kanban-board");
    const column = board?.querySelectorAll<HTMLElement>(".kanban-column")[Number(select.value)];
    if (!board || !column) return;
    const targetLeft = board.scrollLeft + column.getBoundingClientRect().left - board.getBoundingClientRect().left;
    board.scrollLeft = Math.max(0, targetLeft);
    actions.rememberBoardColumn(column.dataset.boardCollection || "", column.dataset.boardColumn || "");
  });
  const boardJump = root.querySelector<HTMLSelectElement>("[data-board-jump]");
  const board = root.querySelector<HTMLElement>(".kanban-board");
  board?.addEventListener("wheel", event => {
    if (event.defaultPrevented || event.ctrlKey) return;
    const cards = (event.target as HTMLElement).closest<HTMLElement>(".kanban-cards");
    if (!cards) return;
    const horizontal = event.shiftKey && !event.deltaX ? event.deltaY : Math.abs(event.deltaX) >= Math.abs(event.deltaY) ? event.deltaX : 0;
    if (horizontal) {
      const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? board.clientWidth : 1;
      board.scrollLeft += horizontal * scale;
      event.preventDefault();
      return;
    }
    // Chromium can latch the gesture inside a column at a subpixel scroll boundary.
    // Keep normal column scrolling native; hand off only outward movement at its edge.
    const max = cards.scrollHeight - cards.clientHeight;
    const atEdge = event.deltaY < 0 ? cards.scrollTop <= 1 : event.deltaY > 0 && cards.scrollTop >= max - 1;
    const view = board.ownerDocument.defaultView;
    if (atEdge && view) {
      const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? view.innerHeight : 1;
      view.scrollBy({ top: event.deltaY * scale, behavior: "instant" });
      event.preventDefault();
    }
  }, { passive: false });
  board?.addEventListener("scroll", () => {
    if (!boardJump) return;
    const columns = Array.from(board.querySelectorAll<HTMLElement>(".kanban-column"));
    const edge = board.getBoundingClientRect().left;
    const nearest = columns.reduce((best, column, index) => Math.abs(column.getBoundingClientRect().left - edge) < Math.abs(columns[best].getBoundingClientRect().left - edge) ? index : best, 0);
    if (columns[nearest]) boardJump.value = String(nearest);
  }, { passive: true });

  root.querySelector<HTMLSelectElement>("[data-group-jump]")?.addEventListener("change", event => {
    const id = (event.currentTarget as HTMLSelectElement).value;
    if (id) actions.jumpToGroup?.(id);
  });
  root.querySelector<HTMLDetailsElement>(".task-more-details")?.addEventListener("toggle", event => actions.taskDetailsToggled?.((event.currentTarget as HTMLDetailsElement).open));
  root.querySelectorAll<HTMLDetailsElement>("[data-composer-group]").forEach(group => group.addEventListener("toggle", () => actions.composerGroupToggled?.(group.dataset.composerGroup || "", group.open)));
  root.querySelectorAll<HTMLElement>("[data-dependency-task]").forEach(item => item.addEventListener("click", () => invoke(() => actions.openDependency?.(item.dataset.dependencyTask || ""))));
  root.querySelectorAll<HTMLFormElement>("[data-create-child-task]").forEach(form => form.addEventListener("submit", event => {
    event.preventDefault();
    const input = form.elements.namedItem("title") as HTMLInputElement;
    const title = input.value.trim();
    if (title) invoke(() => actions.createChildTask?.(form.dataset.createChildTask || "", title));
  }));
  root.querySelectorAll<HTMLButtonElement>("[data-reorder-child]").forEach(button => button.addEventListener("click", () => invoke(() => actions.reorderChildTask?.(button.dataset.parent || "", button.dataset.child || "", button.dataset.reorderChild === "up" ? -1 : 1))));
  root.querySelectorAll<HTMLButtonElement>("[data-attach-child-task]").forEach(button => button.addEventListener("click", () => invoke(() => actions.attachChildTask?.(button.dataset.attachChildTask || ""))));
  root.querySelectorAll<HTMLButtonElement>("[data-detach-child-task]").forEach(button => button.addEventListener("click", () => invoke(() => actions.detachChildTask?.(button.dataset.detachChildTask || ""))));
  root.querySelectorAll<HTMLElement>("[data-resolve-conflicts]").forEach(item => item.addEventListener("click", () => invoke(() => actions.resolveConflicts?.(item.dataset.resolveConflicts || ""))));
  root.querySelectorAll<HTMLElement>("[data-load-previews]").forEach(item => item.addEventListener("click", () => invoke(() => actions.loadPreviews?.(item.dataset.loadPreviews || "", item))));
  root.querySelectorAll<HTMLElement>("[data-template]").forEach(item => item.addEventListener("click", () => invoke(() => actions.createFromTemplate?.(item.dataset.template || ""))));
  root.querySelector<HTMLElement>("[data-role=subtask-list]")?.addEventListener("click", event => {
    const remove = (event.target as HTMLElement).closest<HTMLElement>("[data-action=remove-subtask]");
    const row = remove?.closest<HTMLElement>("[data-subtask]");
    const list = row?.parentElement;
    if (!row || !list) return;
    const next = row.nextSibling;
    row.remove();
    actions.removeSubtask?.(row, list, next);
  });
  root.querySelectorAll<HTMLButtonElement>("[data-move-task]").forEach(button => button.addEventListener("click", () => {
    const row = button.closest<HTMLElement>(".todo-row");
    if (!row?.dataset.todo) return;
    invoke(() => actions.moveTask?.(row.dataset.todo || "", button.dataset.moveTask === "up" ? -1 : 1));
  }));
  root.querySelectorAll<HTMLButtonElement>("[data-move-subtask]").forEach(button => button.addEventListener("click", () => {
    const row = button.closest<HTMLElement>(".subtask-item");
    const list = row?.parentElement;
    if (!row || !list) return;
    const rows = Array.from(list.querySelectorAll<HTMLElement>(":scope > .subtask-item"));
    const direction: -1 | 1 = button.dataset.moveSubtask === "up" ? -1 : 1;
    const index = rows.indexOf(row);
    const destination = index + direction;
    if (destination < 0 || destination >= rows.length) return;
    const originalIds = rows.map(item => item.dataset.subtask || "");
    const reordered = [...rows];
    [reordered[index], reordered[destination]] = [reordered[destination], reordered[index]];
    reordered.forEach(item => list.append(item));
    actions.moveSubtask?.(row, list, direction, originalIds);
  }));
}
